import { useEffect, useState, type FormEvent } from 'react';
import type { State } from '../shared/model';

interface Workspace {
  enabled: boolean;
  available: boolean;
  identities: { id: string; memberId: string; email: string; name: string }[];
  invitations: {
    id: string;
    name: string;
    email: string;
    memberId: string | null;
    role: string;
    expiresAt: number;
  }[];
}
export function EmailInvites({ data }: { data: State }) {
  const [workspace, setWorkspace] = useState<Workspace>(),
    [memberId, setMemberId] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  async function call(path: string, method = 'GET', body?: unknown) {
    const response = await fetch(path, {
      method,
      headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Não foi possível atualizar os convites.');
    return result;
  }
  const load = async () => setWorkspace(await call('/api/team/email-invites'));
  useEffect(() => {
    void load().catch((reason) => setError(reason.message));
  }, []);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget),
      selected = data.members.find((member) => member.id === memberId);
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await call('/api/team/email-invites', 'POST', {
        email: String(form.get('email') ?? '').trim(),
        name: selected?.name ?? form.get('name'),
        role: selected?.role ?? form.get('role'),
        ...(selected ? { memberId: selected.id } : {}),
      });
      await load();
      setNotice(
        'Endereço convidado por 24 horas. A pessoa deve solicitar seu link na tela de entrada; nenhum e-mail é enviado ao salvar este cadastro.',
      );
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-labelledby="email-invites-heading">
      <h3 id="email-invites-heading">Acesso editorial por e-mail</h3>
      <p>
        Convide um endereço específico. Para quem já usa Facebook, selecione o membro existente para
        manter suas campanhas, permissões e histórico.
      </p>
      {workspace && !workspace.available && (
        <p className="notice">
          O envio por e-mail está desativado ou aguarda configuração. Cadastrar um endereço não
          ativa o remetente.
        </p>
      )}
      {error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      <form onSubmit={submit}>
        <label className="field">
          <span>Identidade do convidado</span>
          <select
            value={memberId}
            onChange={(event) => setMemberId(event.target.value)}
            disabled={busy}
          >
            <option value="">Novo membro da equipe</option>
            {data.members
              .filter((member) => member.active)
              .map((member) => (
                <option value={member.id} key={member.id}>
                  {member.name} · membro existente
                </option>
              ))}
          </select>
        </label>
        {!memberId && (
          <>
            <label className="field">
              <span>Nome para o acesso por e-mail</span>
              <input name="name" required maxLength={250} disabled={busy} />
            </label>
            <label className="field">
              <span>Papel inicial por e-mail</span>
              <select name="role" disabled={busy}>
                <option value="editor">Editor</option>
                <option value="approver">Aprovador</option>
                <option value="reader">Leitor</option>
              </select>
            </label>
          </>
        )}
        <label className="field">
          <span>Endereço de e-mail convidado</span>
          <input
            name="email"
            type="email"
            autoComplete="off"
            required
            maxLength={254}
            disabled={busy}
          />
        </label>
        <button disabled={busy}>{busy ? 'Salvando…' : 'Cadastrar convite por e-mail'}</button>
      </form>
      {workspace?.identities.map((identity) => (
        <p key={identity.id}>
          {identity.name} · {identity.email} · e-mail verificado
        </p>
      ))}
      {workspace?.invitations.map((invite) => (
        <div className="team-member" key={invite.id}>
          <span>
            {invite.name} · {invite.email}
            <small>
              {' '}
              Convite pendente até {new Date(invite.expiresAt).toLocaleString('pt-BR')}
            </small>
          </span>
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await call(
                  `/api/team/email-invites/${encodeURIComponent(invite.id)}`,
                  'DELETE',
                  {},
                );
                await load();
              } catch (reason) {
                setError((reason as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Revogar convite de {invite.name}
          </button>
        </div>
      ))}
    </section>
  );
}
